import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Grid, Typography, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { useDispatch, useSelector } from "react-redux";
import {
  registerUser,
  saveValue,
  userSelector,
  validateEmail,
} from "../../store/slices/userSlice";

import regex from "../../utils/regex";
import readableABNString from "../../utils/readableABNString";

import ggfLogo from "../../ggf.gif";
import { pageRoutes, registrationMessages, userTypes } from "../../constants";
import { StepMessage } from "../../components/Auth/common";
import InvitationNotice from "./InvitationNotice";
import OrganisationSearch from "./OrganisationSearch";
import RegistrationReceived from "./RegistrationReceived";
import useInvitation from "./useInvitation";

export default function Application() {
  const dispatch = useDispatch();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [firstNameErrorText, setFirstNameErrorText] = useState(" ");
  const [lastNameErrortext, setLastNameErrorText] = useState(" ");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState(" ");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  // Picked in the organisation field: { details, isNew }
  const [organisation, setOrganisation] = useState(null);
  const [submitError, setSubmitError] = useState("");
  // The server's answer once the account is created
  const [registration, setRegistration] = useState(null);

  const { emailInUse, isCreatingAccount } = useSelector(userSelector);
  // From an invitation link, the broker joins the organisation they were invited to with the email address it was
  // sent to
  const invitation = useInvitation();

  useEffect(() => {
    if (!invitation.details) return;

    setFirstName(invitation.details.firstName);
    setEmail(invitation.details.email);
  }, [invitation.details]);

  const handleRegistration = async () => {
    setSubmitError("");

    const result = await dispatch(
      registerUser({
        firstName,
        lastName,
        email,
        phoneNumber,
        password,
        // The invitation decides the organisation
        ...(invitation.details
          ? { invitationToken: invitation.token }
          : { organisation: organisation.details }),
      })
    );

    if (registerUser.fulfilled.match(result)) return setRegistration(result.payload.data);

    const { message } = result.payload || {};
    setSubmitError(typeof message === "string" ? message : registrationMessages.GENERIC_ERROR);
  };

  function handleFirstName(event) {
    setFirstName(event.target.value);

    if (regex.name.test(event.target.value)) {
      setFirstNameError(false);

      return setFirstNameErrorText(" ");
    }

    setFirstNameError(true);
    setFirstNameErrorText(
      "Name can contain only letters, dashes (-) or spaces."
    );
  }

  function handleLastName(event) {
    setLastName(event.target.value);

    if (regex.name.test(event.target.value)) {
      setLastNameError(false);

      return setLastNameErrorText(" ");
    }

    setLastNameError(true);
    setLastNameErrorText(
      "Name can contain only letters, dashes (-) or spaces."
    );
  }

  function handleEmail(event) {
    setEmail(event.target.value.trim());

    if (regex.email.test(event.target.value.toLowerCase())) {
      setEmailError(false);
      dispatch(saveValue({ emailInUse: false }));
      return setEmailErrorText(" ");
    }

    setEmailError(true);
    setEmailErrorText("Invalid email");
  }

  async function handleCheckEmail() {
    // An invitation's address was checked with the invitation
    if (email && !invitation.details) {
      await dispatch(validateEmail(email)).unwrap();
    }
  }

  function handlePhoneNumber(event) {
    // Digits only and max 10, so spaces/extra digits can't be entered (also when pasted)
    const value = event.target.value.replace(/\D/g, "").slice(0, 10);

    setPhoneNumber(value);
    setPhoneNumberError(!regex.mobileNumber.test(value));
  }

  function handlePassword(event) {
    setPassword(event.target.value);

    regex.password.test(event.target.value)
      ? setPasswordError(false)
      : setPasswordError(true);
  }

  useEffect(() => {
    if (emailInUse) {
      setEmailError(true);
      return setEmailErrorText("Email address already in use.");
    }

    setEmailError(false);
    return setEmailErrorText(" ");
  }, [emailInUse]);

  const canSubmit =
    firstName &&
    lastName &&
    email &&
    phoneNumber &&
    password &&
    (organisation || invitation.details) &&
    !firstNameError &&
    !lastNameError &&
    !emailError &&
    !phoneNumberError &&
    !passwordError &&
    !isCreatingAccount;

  return (
    <>
      <Grid
        container
        item
        minHeight="100vh"
        width="100%"
        justifyContent="center"
        alignItems="center"
        direction="row-reverse"
        sx={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          p: 2,
        }}
      >
        <Grid
          container
          item
          xs={12}
          sm={10}
          md={6}
          xl={5}
          justifyContent="center"
          alignItems="center"
          direction="column"
          textAlign="center"
          sx={{
            bgcolor: "white",
            borderRadius: 4,
            boxShadow: 4,
            p: { xs: 4, sm: 6 },
            maxWidth: 500,
          }}
        >
          <img
            src={ggfLogo}
            alt="Logo"
            style={{
              height: "120px",
              marginBottom: "16px",
              imageRendering: "-webkit-optimize-contrast",
            }}
          />

          {registration ? (
            <RegistrationReceived
              firstName={firstName}
              email={email}
              organisationName={registration.organisationName}
              isNewOrganisation={registration.isNewOrganisation}
              isInvited={registration.isInvited}
            />
          ) : invitation.isChecking || invitation.error ? (
            <InvitationNotice
              isChecking={invitation.isChecking}
              error={invitation.error}
            />
          ) : (
            <>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{ mb: invitation.details ? 1 : 3, color: "primary.main" }}
              >
                Sign up to the broker portal
              </Typography>

              {invitation.details && (
                <Typography sx={{ mb: 3, color: "text.secondary" }}>
                  You’ve been invited to join {invitation.details.organisationName}.
                </Typography>
              )}

              {/* First + Last Name in one row */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="text"
                    required
                    name="firstName"
                    label="First name"
                    autoComplete="first-name"
                    value={firstName}
                    inputProps={{ maxLength: 24 }}
                    onChange={handleFirstName}
                    error={firstNameError}
                    helperText={firstNameErrorText}
                    FormHelperTextProps={{
                      sx: {
                        mt: 0.5,
                        fontSize: "0.75rem",
                        display: firstNameError ? "block" : "none",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="text"
                    required
                    name="lastName"
                    label="Last name"
                    autoComplete="last-name"
                    value={lastName}
                    inputProps={{ maxLength: 24 }}
                    onChange={handleLastName}
                    error={lastNameError}
                    helperText={lastNameErrortext}
                    FormHelperTextProps={{
                      sx: {
                        mt: 0.5,
                        fontSize: "0.75rem",
                        display: lastNameError ? "block" : "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                type="email"
                required
                name="email"
                label="Email"
                autoComplete="email"
                value={email}
                onChange={handleEmail}
                helperText={emailErrorText}
                error={emailError}
                onBlur={handleCheckEmail}
                inputProps={{ maxLength: 50 }}
                InputProps={{ readOnly: Boolean(invitation.details) }}
                sx={{ mb: 3 }}
                FormHelperTextProps={{
                  sx: {
                    mt: 0.5,
                    fontSize: "0.75rem",
                    display: emailError ? "block" : "none",
                  },
                }}
              />

              <TextField
                fullWidth
                type="tel"
                required
                name="phoneNumber"
                label="Mobile number"
                autoComplete="tel"
                value={phoneNumber}
                onChange={handlePhoneNumber}
                error={phoneNumberError}
                helperText="Enter a valid Australian mobile number, e.g. 0412345678."
                inputProps={{ inputMode: "numeric" }}
                sx={{ mb: 3 }}
                FormHelperTextProps={{
                  sx: {
                    mt: 0.5,
                    fontSize: "0.75rem",
                    display: phoneNumberError ? "block" : "none",
                  },
                }}
              />

              {invitation.details ? (
                <TextField
                  fullWidth
                  name="organisation"
                  label="Business/organisation"
                  value={invitation.details.organisationName}
                  InputProps={{ readOnly: true }}
                  helperText={`ABN ${readableABNString(invitation.details.abn)} · You’ll join as ${
                    invitation.details.userType === userTypes.ADMIN ? "an admin" : "a user"
                  }.`}
                  sx={{ mb: 3 }}
                  FormHelperTextProps={{ sx: { mt: 0.5, fontSize: "0.75rem" } }}
                />
              ) : (
                <OrganisationSearch
                  organisation={organisation}
                  onChange={setOrganisation}
                  disabled={isCreatingAccount}
                />
              )}

              <TextField
                fullWidth
                type="password"
                required
                name="password"
                label="Password"
                autoComplete="new-password"
                value={password}
                onChange={handlePassword}
                error={passwordError}
                helperText="Must contain an uppercase letter, a number, a special character (!@$%&*) and be at least 8 characters long."
                inputProps={{ maxLength: 32 }}
                sx={{ mb: 2 }}
                FormHelperTextProps={{
                  sx: {
                    mt: 0.5,
                    fontSize: "0.75rem",
                    display: passwordError ? "block" : "none",
                  },
                }}
              />

              <StepMessage error={submitError} />

              <LoadingButton
                fullWidth
                loading={isCreatingAccount}
                loadingPosition="end"
                variant="contained"
                disabled={!canSubmit}
                onClick={handleRegistration}
                sx={{
                  mt: 2,
                  mb: 3,
                  py: 1.2,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                {isCreatingAccount ? "Creating account..." : "Create my account"}
              </LoadingButton>

              <Typography variant="body2">
                Already have an account?{" "}
                <Link
                  to={pageRoutes.LOGIN}
                  style={{
                    fontWeight: 500,
                  }}
                >
                  Login
                </Link>
              </Typography>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
}
